import React from 'react';

interface DocsProps {
  onLaunchPlatform?: () => void;
}

const Docs: React.FC<DocsProps> = ({ onLaunchPlatform }) => {

  return (
    <main className="bg-white text-gray-900 min-h-screen overflow-hidden">
      {/* Background Effects */}
      <div className="fixed inset-0 overflow-hidden z-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-green-100/50 via-transparent to-transparent"></div>
        <div className="absolute bottom-0 right-0 w-full h-full bg-gradient-to-tl from-green-100/30 via-transparent to-transparent"></div>
        
        {/* Grid pattern */}
        <div className="absolute inset-0 opacity-3"
          style={{
            backgroundImage: `
              linear-gradient(rgba(80, 220, 160, 0.08) 1px, transparent 1px),
              linear-gradient(90deg, rgba(80, 220, 160, 0.08) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }}
        ></div>
      </div>

      {/* Background Text */}
      <div className="fixed inset-0 flex items-center justify-center z-0 pointer-events-none">
        <div className="text-[20vw] font-black text-green-100/30 leading-none tracking-tighter select-none">
          DOCS
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 px-4 py-20">
        <div className="max-w-6xl mx-auto">
          
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black mb-6 bg-gradient-to-r from-green-600 via-green-500 to-green-600 bg-clip-text text-transparent">
              Documentation
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 leading-relaxed max-w-4xl mx-auto">
              Everything you need to know about using EKKO platform, from getting started to advanced trading strategies.
            </p>
          </div>

          {/* Search Bar */}
          <div className="mb-12">
            <div className="relative max-w-2xl mx-auto">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search documentation..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-4 border border-gray-300 rounded-xl bg-white text-gray-900 placeholder-gray-500 focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none"
              />
            </div>
          </div>

          {/* Categories Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredCategories.map((category) => (
              <div
                key={category.id}
                className="group bg-white/90 backdrop-blur-xl rounded-3xl border border-green-200/50 p-8 shadow-[0_0_30px_rgba(80,220,160,0.1)] hover:shadow-[0_0_50px_rgba(80,220,160,0.15)] transition-all duration-500 hover:scale-105 hover:border-green-300/50"
              >
                {/* Hover glow effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-green-100/20 via-transparent to-transparent rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <div className="relative z-10">
                  {/* Icon */}
                  <div className="w-16 h-16 rounded-2xl bg-green-100/50 border border-green-200/50 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <category.icon className="h-8 w-8 text-green-600" />
                  </div>
                  
                  {/* Content */}
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-green-600 transition-colors duration-300">
                    {category.title}
                  </h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {category.description}
                  </p>
                  
                  {/* Articles List */}
                  <div className="space-y-2 mb-6">
                    {category.articles.slice(0, 3).map((article) => (
                      <div key={article.id} className="flex items-center gap-2 text-sm text-gray-600 hover:text-green-600 transition-colors cursor-pointer">
                        <FileText className="h-4 w-4" />
                        <span>{article.title}</span>
                      </div>
                    ))}
                    {category.articles.length > 3 && (
                      <div className="text-sm text-gray-500">
                        +{category.articles.length - 3} more articles
                      </div>
                    )}
                  </div>
                  
                  {/* View All Button */}
                  <button className="w-full bg-green-100/50 border border-green-300/50 text-green-700 py-3 px-4 rounded-xl font-medium hover:bg-green-100 transition-colors flex items-center justify-center gap-2">
                    View All Articles
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Quick Start Section */}
          <div className="mt-20">
            <div className="bg-white/90 backdrop-blur-xl rounded-3xl border border-green-200/50 p-12 shadow-[0_0_40px_rgba(80,220,160,0.1)]">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Quick Start Guide
                </h2>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  New to EKKO? Follow these essential steps to get started with trading on our platform.
                </p>
              </div>
              
              <div className="grid md:grid-cols-3 gap-8">
                {quickStart.map((step, index) => (
                  <div key={step.id} className="text-center">
                    <div className="w-16 h-16 rounded-2xl bg-green-100/50 border border-green-200/50 flex items-center justify-center mx-auto mb-6">
                      <span className="text-2xl font-bold text-green-600">{index + 1}</span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                    <p className="text-gray-600 mb-4">{step.description}</p>
                    <button className="text-green-600 font-medium hover:text-green-700 transition-colors">
                      Read Guide →
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Help Section */}
          <div className="mt-16 text-center">
            <div className="bg-white/90 backdrop-blur-xl rounded-2xl border border-green-200/50 p-8 shadow-[0_0_30px_rgba(80,220,160,0.1)]">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Need More Help?
              </h3>
              <p className="text-gray-600 mb-6">
                Can't find what you're looking for? Our support team is here to help.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button className="bg-green-100 border border-green-300 text-green-700 font-medium px-6 py-3 rounded-xl hover:bg-green-200 transition-colors flex items-center justify-center gap-2">
                  <MessageCircle className="h-5 w-5" />
                  Contact Support
                </button>
                <button 
                  onClick={() => window.open('https://discord.gg/ekko', '_blank')}
                  className="bg-white border border-gray-300 text-gray-700 font-medium px-6 py-3 rounded-xl hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                >
                  <Users className="h-5 w-5" />
                  Join Community
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Docs; 